import { useRetainedValue } from "../hooks/useRetainedValue.js";
import {
  PastedContextDialog,
  pastedContextLabel,
  type PastedContextItem,
} from "./PastedContext.js";
import { ComposerInputBox } from "./PlayerAIComposerInput.js";
import { ComposerNotices } from "./PlayerAIComposerNotices.js";
import { ComposerReplyPreview } from "./ComposerReplyPreview.js";
import { ComposerTypingLine } from "./ComposerTypingLine.js";
import {
  ComposerPauseNotice,
  type PlayerAIPauseNotice,
  type PlayerAIRunControl,
} from "./ComposerRunControls.js";
import type { ComposerMentionOption } from "./PlayerAIComposerSuggestions.js";
import type {
  ClipboardEvent,
  FormEvent,
  KeyboardEvent,
  MutableRefObject,
  ReactNode,
} from "react";

export type { ComposerMentionOption } from "./PlayerAIComposerSuggestions.js";

/**
 * Everything the composer needs to draw and edit attached pasted context. One
 * object because `ChatBody` is rendered from two shells, and eleven loose props
 * would have to be kept in step at both.
 */
export interface ComposerPastedContext {
  readonly items: readonly PastedContextItem[];
  readonly editingIndex: number;
  readonly editing: PastedContextItem | undefined;
  readonly overLimit: boolean;
  readonly composedLength: number;
  readonly maxLength: number | undefined;
  readonly onPaste: (event: ClipboardEvent<HTMLTextAreaElement>) => void;
  readonly onEdit: (id: string) => void;
  readonly onRemove: (id: string) => void;
  readonly onSaveEdit: (next: string) => void;
  readonly onCancelEdit: () => void;
}

/** `nextPhaseCommand`'s availability shape (PlayerAIRailProps). */
interface ComposerSuggestionAvailability {
  readonly available: boolean;
  readonly disabled?: boolean | undefined;
}

/** The mention picker's state, owned by the rail (Task 6). */
export interface ComposerMentions {
  readonly available: boolean;
  readonly menuOpen: boolean;
  readonly options: readonly ComposerMentionOption[];
  readonly status?: "loading" | "error" | "ready" | undefined;
  readonly onRetry?: (() => void) | undefined;
  readonly activeIndex: number;
  readonly onSelect: (option: ComposerMentionOption) => void;
}

export interface PlayerAIComposerProps {
  pastedContext: ComposerPastedContext;
  draft: string;
  setDraft: (value: string, caret: number, inputType?: string) => void;
  onSubmit: (e: FormEvent) => void;
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  composerRef: MutableRefObject<HTMLTextAreaElement | null>;
  composerDisabled: boolean;
  composerReadOnly: boolean;
  composerPlaceholder?: string | undefined;
  composerStatusSlot?: ReactNode | undefined;
  composerMetadataSlot?: ReactNode | undefined;
  composerContextAttachments?:
    | readonly { id: string; name: string }[]
    | undefined;
  onRemoveContextAttachment?: ((id: string) => void) | undefined;
  composerContextNotice?: string | undefined;
  composerLeadingSlot?: ReactNode | undefined;
  mentions: ComposerMentions;
  nextPhaseCommand?: ComposerSuggestionAvailability | undefined;
  slashMenuOpen: boolean;
  onSelectNextPhaseCommand: () => void;
  composerFooterSlot?: ReactNode | undefined;
  composerHintSlot?: ReactNode | undefined;
  /** Task 7 — the response the next message replies to. */
  reply?: { readonly text: string; readonly onCancel: () => void } | undefined;
  /** Task 15 — other participants typing; present reserves the line. */
  typingNames?: readonly string[] | undefined;
  /** Task 14 */
  runControl?: PlayerAIRunControl | undefined;
  groupConversation?: boolean | undefined;
  pauseNotice?: PlayerAIPauseNotice | undefined;
}

/**
 * The Player AI composer form (PB-5422 chat-parts-private): assembles the
 * notices region (PlayerAIComposerNotices.tsx), the input box
 * (PlayerAIComposerInput.tsx, which also holds the suggestion listboxes from
 * PlayerAIComposerSuggestions.tsx), PastedContextDialog and the footer/hint
 * row. Split across these files to keep each under the 200-line soft cap
 * (packages/ui/CLAUDE.md invariant 6).
 */
export function PlayerAIComposer({
  pastedContext,
  draft,
  setDraft,
  onSubmit,
  onKeyDown,
  composerRef,
  composerDisabled,
  composerReadOnly,
  composerPlaceholder,
  composerStatusSlot,
  composerMetadataSlot,
  composerContextAttachments,
  onRemoveContextAttachment,
  composerContextNotice,
  composerLeadingSlot,
  mentions,
  nextPhaseCommand,
  slashMenuOpen,
  onSelectNextPhaseCommand,
  composerFooterSlot,
  composerHintSlot,
  reply,
  typingNames,
  runControl,
  groupConversation,
  pauseNotice,
}: PlayerAIComposerProps) {
  /* Retained so the edit dialog can render what it was editing while it closes
     — `pastedContext.editing` is already null on that frame (ADR-0091). */
  const editingPastedText = useRetainedValue(pastedContext.editing);

  return (
    // Composer. Only the input row is fixed (PB-5381): the region above the
    // input box and the chips inside it shrink and scroll when the surface
    // is short, so neither can push Send out of view.
    <form
      onSubmit={onSubmit}
      className="flex min-h-0 min-w-0 flex-col border-t border-pb-border/50 p-3 bg-pb-rail"
    >
      {typingNames !== undefined ? (
        <ComposerTypingLine names={typingNames} />
      ) : null}
      <ComposerNotices
        overLimit={pastedContext.overLimit}
        composedLength={pastedContext.composedLength}
        maxLength={pastedContext.maxLength}
        statusSlot={
          pauseNotice || composerStatusSlot ? (
            <div className="space-y-1.5">
              {pauseNotice ? (
                <ComposerPauseNotice notice={pauseNotice} />
              ) : null}
              {composerStatusSlot}
            </div>
          ) : undefined
        }
        metadataSlot={composerMetadataSlot}
        contextNotice={composerContextNotice}
      />
      {reply ? (
        <ComposerReplyPreview text={reply.text} onCancel={reply.onCancel} />
      ) : null}
      <ComposerInputBox
        runControl={runControl}
        groupConversation={groupConversation}
        pastedContext={pastedContext}
        composerRef={composerRef}
        draft={draft}
        setDraft={setDraft}
        onKeyDown={onKeyDown}
        composerDisabled={composerDisabled}
        composerReadOnly={composerReadOnly}
        composerPlaceholder={composerPlaceholder}
        composerContextAttachments={composerContextAttachments}
        onRemoveContextAttachment={onRemoveContextAttachment}
        composerLeadingSlot={composerLeadingSlot}
        mentionsAvailable={mentions.available}
        mentionMenuOpen={mentions.menuOpen}
        mentionOptions={mentions.options}
        mentionStatus={mentions.status}
        onRetryMentions={mentions.onRetry}
        activeMentionIndex={mentions.activeIndex}
        onSelectMention={mentions.onSelect}
        nextPhaseCommand={nextPhaseCommand}
        slashMenuOpen={slashMenuOpen}
        onSelectNextPhaseCommand={onSelectNextPhaseCommand}
      />
      {/* The dialog holds its own closing frame, so the text it is editing is
          retained: on the frame the edit clears, `editing` is already null
          while the dialog is still painted (ADR-0091). */}
      <PastedContextDialog
        open={Boolean(pastedContext.editing)}
        label={pastedContextLabel(
          pastedContext.editingIndex,
          pastedContext.items.length,
        )}
        text={editingPastedText?.text ?? ""}
        onCancel={pastedContext.onCancelEdit}
        onSave={pastedContext.onSaveEdit}
      />
      {/* Optional host controls and the keyboard hint. */}
      <div className="mt-1.5 flex shrink-0 items-center justify-between gap-2">
        {composerFooterSlot ? <div>{composerFooterSlot}</div> : null}
        <div className="text-dense-2 text-pb-text-muted text-end ms-auto">
          {composerHintSlot ?? "Enter to send · ⌘/Ctrl+Enter for a new line"}
        </div>
      </div>
    </form>
  );
}
